import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { getErrorComponents, isSmallWindow } from "services/util";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import PageLayout from "components/page/PageLayout/PageLayout";
import TalaeraRoutes from "components/routes/TalaeraRoutes";
import ResizeBehaviour from "components/ResizeBehaviour/ResizeBehaviour";

import { LayoutFooter } from "./LayoutFooter";
import TalaeraBackground from "components/TalaeraBackground";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "../LanguageSelector";
import useUser from "hooks/useUser";
import { ErrorBoundary } from "../ErrorBoundary";
import { RouteList } from "components/routes/RouteList";
import { OnboardingPopupProvider } from "../../contexts/OnboardingContext";

const DEFAULT_LANG = "en";

const getDefaultLanguage = () => {
  if (localStorage.getItem("language")) {
    return String(localStorage.getItem("language"));
  }
  let lang = DEFAULT_LANG;
  if (navigator.language === "en" || navigator.language.indexOf("en-") > -1) {
    lang = "en";
  } else if (navigator.language === "ko" || navigator.language.indexOf("ko-") > -1) {
    lang = "ko";
  } else if (navigator.language === "de" || navigator.language.indexOf("de-") > -1) {
    lang = "de";
  } else if (navigator.language === "es" || navigator.language.indexOf("es-") > -1) {
    lang = "es";
  }
  localStorage.setItem("language", lang);
  return lang;
};

interface LayoutProps {
  isAuthenticated: boolean | null;
  showToast: (message: string) => void;
}

export function Layout({ isAuthenticated, showToast }: LayoutProps) {
  const [smallWindow, setSmallWindow] = useState(isSmallWindow());
  const [language, setLanguage] = useState(getDefaultLanguage());
  const { i18n } = useTranslation();
  const location = useLocation();
  const user = useUser();
  const userLanguage = user?.localization_lang || "";

  useEffect(() => {
    const uiLanguage = userLanguage || navigator.language;
    let supportedLanguages = LANGUAGES.map(v => v.value);

    if (i18n.language !== uiLanguage) {
      i18n.changeLanguage(supportedLanguages.includes(uiLanguage) ? uiLanguage : "en");
    }
  }, [userLanguage, i18n, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <TalaeraBackground>
        <TalaeraSpinner />
      </TalaeraBackground>
    );
  }

  const resize = () => {
    setSmallWindow(isSmallWindow());
  };

  const handleLangChange = (newLang: string) => (event: Event) => {
    event.preventDefault();
    if (language !== newLang) {
      setLanguage(newLang);
      localStorage.setItem("language", newLang);
    }
  };

  const noHeaderFooter =
    !user ||
    TalaeraRoutes.SIGN_OUT.equals(location.pathname) ||
    TalaeraRoutes.SELF_SIGN_UP.equals(location.pathname) ||
    TalaeraRoutes.SELF_SIGNUP_VERIFY_INDIVIDUAL.equals(location.pathname);

  return (
    <>
      <ResizeBehaviour resize={resize} />
      <Helmet htmlAttributes={{ lang: language }}>
        <title>Talaera</title>
      </Helmet>
      <PageLayout
        footer={
          <ErrorBoundary fallback={getErrorComponents("Something went wrong.")}>
            <LayoutFooter
              noHeaderFooter={noHeaderFooter}
              handleLangChange={handleLangChange}
              smallWindow={smallWindow}
              language={language}
            />
          </ErrorBoundary>
        }
        noHeader={noHeaderFooter}
        routes={
          <OnboardingPopupProvider>
            <RouteList smallWindow={false} showToast={showToast} />
          </OnboardingPopupProvider>
        }
        location={location}
      />
    </>
  );
}
